import React, {useMemo} from 'react'
import {Box, Flex, Image, Text, Link} from '@chakra-ui/react'
import {
    articleDate,
    articleDescription,
    articleHeader,
    articleImage,
    articleMore,
    dividerStyle,
    headerStyle
} from './ticket.style'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {Link as RouteLink} from 'react-router-dom'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {pxToRem} from '../../../../utills/pxToRem'
import {MONTH_NAMES_ABBR} from '../../../../constants'
import {blogRoute} from '../../../../utills/url'

const Ticket = ({articles}) => {
    const groupedArticles = useMemo(() => {
        if (!articles) return []
        const groups = articles.reduce((acc, article) => {
            const publishDate = article.publishDate
            const parts = publishDate.split(' ')
            const monthAbbr = parts[0]
            const year = parts[2]

            const key = `${monthAbbr}-${year}`
            const monthYear = `${MONTH_NAMES_ABBR[monthAbbr]}, ${year}`

            if (!acc[key]) {
                acc[key] = {
                    monthYear,
                    articles: []
                }
            }
            acc[key].articles.push(article)
            return acc
        }, {})

        return Object.values(groups)
    }, [articles])

    const navigate = useNavigation()
    const intl = useIntl()

    return (
        <Box pr={{md: pxToRem(20)}}>
            {groupedArticles.map((group, groupIndex) => (
                <Box key={`${group.monthYear}`}>
                    {groupIndex > 0 && <Box sx={dividerStyle} />}
                    <Text textAlign="center" sx={headerStyle}>
                        {group.monthYear}
                    </Text>
                    {group.articles.map((article, articleIndex) => (
                        <Box key={article?.id}>
                            <Box mb={{xl: pxToRem(10)}}>
                                <Flex
                                    flexDir={{base: 'column', md: 'row'}}
                                    gap={{base: pxToRem(10), md: pxToRem(20)}}
                                >
                                    {!article?.featuredMedia ? (
                                        <Image
                                            src={getAssetUrl(
                                                'static/img/blog-list/blplaceholder.png'
                                            )}
                                            minW={{md: pxToRem(200)}}
                                            w={{md: pxToRem(200)}}
                                            aspectRatio="1.67"
                                            align={{base: 'center', md: 'initial'}}
                                            sx={articleImage}
                                        />
                                    ) : article.isFeatureMediaVideo ? (
                                        <Box>
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    paddingBottom: '56.25%',
                                                    height: 0,
                                                    overflow: 'hidden',
                                                    width: {base: '100%', md: pxToRem(200)},
                                                    flex: '0 0 auto',
                                                    my: pxToRem(12),
                                                    mr: pxToRem(20)
                                                }}
                                            >
                                                <iframe
                                                    src={article.featuredMedia}
                                                    title={'item.title'}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%'
                                                    }}
                                                ></iframe>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Image
                                            src={article.featuredMedia}
                                            alt={article.title}
                                            minW={{md: pxToRem(200)}}
                                            w={{md: pxToRem(200)}}
                                            aspectRatio="2.5"
                                            align={{base: 'center', md: 'initial'}}
                                            sx={articleImage}
                                        />
                                    )}
                                    <Flex flexDir="column">
                                        <Link
                                            onClick={() =>
                                                navigate(blogRoute(`${article?.id}.html`))
                                            }
                                            sx={articleHeader}
                                        >
                                            <Box
                                                dangerouslySetInnerHTML={{
                                                    __html: article?.title
                                                }}
                                            />
                                        </Link>
                                        <Text sx={articleDate}>{article?.publishDate}</Text>
                                        <Box
                                            sx={articleDescription}
                                            dangerouslySetInnerHTML={{
                                                __html: `${article?.description} [...]`
                                            }}
                                        />
                                        <Link
                                            to={blogRoute(`${article?.id}.html`)}
                                            as={RouteLink}
                                            sx={articleMore}
                                        >
                                            {intl.formatMessage({
                                                id: 'bloglist.more',
                                                defaultMessage: 'More ...'
                                            })}
                                        </Link>
                                    </Flex>
                                </Flex>
                            </Box>
                            {articleIndex < group.articles.length - 1 && <Box sx={dividerStyle} />}
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    )
}

Ticket.propTypes = {
    articles: PropTypes.array
}

export default Ticket
